import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'

import {
    getSyncLogActions,
    getSyncLogColumns,
    getSyncLogFilterItems,
    getSyncLogFilterToggles,
} from '../components/AttendanceSyncLogs/sync-log-list-config'
import { getAllAttendanceSyncLogs } from '../services'

interface SyncLogFilters {
    status: string
    from_date: string
    to_date: string
}

export const useSyncLogList = () => {
    const { t } = useTranslation('workday')

    const { params, filters, queryParams, setParams, setFilter } =
        useUrlSearchParams<SyncLogFilters>({
            defaults: { sort_by: 'sync_log_id', sort_order: 'desc' },
            filterDefaults: { status: '', from_date: '', to_date: '' },
        })

    const { control } = useForm<SyncLogFilters>({
        defaultValues: filters,
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ATTENDANCE_SYNC_LOGS, queryParams],
        queryFn: () => getAllAttendanceSyncLogs(queryParams),
        placeholderData: true,
    })

    // Memoized configs — onViewMetadata will be injected by the component
    const columns = useMemo(() => getSyncLogColumns(t), [t])
    const filterItems = useMemo(() => getSyncLogFilterItems(t), [t])
    const filterToggles = useMemo(() => getSyncLogFilterToggles(t), [t])

    const buildActions = useCallback(
        (onViewMetadata: (row: any) => void) =>
            getSyncLogActions({ t, onViewMetadata }),
        [t]
    )

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        syncLogs: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),

        control,
        setFilter,
        params,

        columns,
        buildActions,
        filterItems,
        filterToggles,

        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
    }
}
